<template>
    <div class="row bt-dash">
        <div class=" col-6">
            <div class="d-flex align-items-center gap-3 form-check checkbox-checked my-3">
                <div>
                    <input type="checkbox" :id="`checkbox-Required`" v-model="compState.required"
                        class="form-check-input" />
                    <label :for="`checkbox-Required`" style="margin: 0;">{{ t('Variation Required') }}</label>
                </div>
                <button class="btn btn-outline-primary-2x" type="button" @click="onSourceClick">
                    <i class="fa fa-info-circle"></i>
                    {{ t('This is a source') }}
                </button>
            </div>
        </div>
        <div class="col-12 p-0 bt-dash">
            <table class="w-100 table" style="border-collapse: collapse;">
                <thead>
                    <tr>
                        <th></th>
                        <th>{{ t('Variation') }}</th>
                        <th>{{ t('Product Number') }}</th>
                        <th>{{ t('Product Name') }}</th>
                        <th></th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            <div
                                class="d-flex justify-content-center align-items-center gap-2 form-check checkbox-checked">
                                <input type="checkbox" :id="`checkbox-source-variable`"
                                    v-model="compState.sourceProduct" class="form-check-input" :disabled="true" />
                                <label :for="`checkbox-source-variable`" style="margin-top: 12px;">{{
                                    compState.sourceProductData.id
                                }}.</label>
                            </div>
                        </td>
                        <td>
                            {{ compState.sourceProductData.variation }}
                        </td>
                        <td>
                            {{ compState.sourceProductData?.productData?.basic?.productNumber || 'N/A' }}
                        </td>
                        <td>
                            {{ compState.sourceProductData?.productData?.basic?.name || 'N/A' }}
                        </td>
                        <td>
                        </td>
                        <td>
                            <button :disabled="!compState.sourceProductData?.productData?.basic?.productNumber"
                                class="btn btn-danger " type="button" @click="onClearSourceData">
                                <i class="fa fa-times"></i> {{ t('Clear') }}
                            </button>
                        </td>
                    </tr>
                    <tr v-for="variation in variationTemplate" :key="variation.id">
                        <td>
                            <div
                                class="d-flex justify-content-center align-items-center gap-2 form-check checkbox-checked">
                                <input type="checkbox" :id="`checkbox-${variation.id}`" v-model="variation.selected"
                                    class="form-check-input" :disabled="true" />
                                <label :for="`checkbox-${variation.id}`" style="margin-top: 12px;">{{ variation.id
                                    }}.</label>
                            </div>
                        </td>
                        <td>
                            {{ variation.variation }}
                        </td>
                        <td>
                            {{ variation?.productData?.basic?.productNumber || 'N/A' }}
                        </td>
                        <td>
                            {{ variation?.productData?.basic?.name || 'N/A' }}
                        </td>
                        <td>
                            <template v-if="variation?.productData?.basic?.productNumber">
                                <button :disabled="!compState.sourceProductData.selected" class="btn btn-secondary"
                                    type="button" data-bs-toggle="modal" data-original-title="test"
                                    data-bs-target="#product-search-modal" @click="onProductSearchOpen(variation.id)">
                                    <i class="fa fa-edit"></i> {{ t('Edit') }}
                                </button>
                            </template>
                            <template v-else>
                                <button :disabled="!compState.sourceProductData.selected" class="btn btn-primary"
                                    type="button" data-bs-toggle="modal" data-original-title="test"
                                    data-bs-target="#product-search-modal" @click="onProductSearchOpen(variation.id)">
                                    <i class="fa fa-plus"></i> {{ t('Add') }}
                                </button>
                            </template>
                        </td>
                        <td>
                            <button :disabled="!canClearVariationData(variation.id)" class="btn btn-danger "
                                type="button" @click="onClearProductData(variation.id)">
                                <i class="fa fa-times"></i> {{ t('Clear') }}
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
    <productSearchModal @on-select="onSelect" />
</template>

<script lang="ts" setup>
import { ref, defineAsyncComponent, onMounted, reactive, computed, watch } from 'vue';
import { useProductsStore } from '@/store/management/productsStore';
import type { VariationItem, Product } from '@/store/management/productsStore';
import Swal from 'sweetalert2';
import { useI18n } from 'vue-i18n'
import { productsLanguage } from '@/core/locales/products-language'

const { t } = useI18n({
  messages: productsLanguage
})

const productSearchModal = defineAsyncComponent(() => import('@/components/managementPages/productsPage/productSearchModal.vue'));
const productStore = useProductsStore()

const variationTemplate = ref([
    {
        selected: false,
        id: '2',
        variation: 'S/R',
        productData: {} as Product
    },
    {
        selected: false,
        id: '3',
        variation: 'L',
        productData: {} as Product
    },
    {
        selected: false,
        id: '4',
        variation: 'XL',
        productData: {} as Product
    },
])

watch(
    () => variationTemplate.value.map(v => v.selected),
    (newVal, oldVal) => {
        newVal.forEach((selected, index) => {
            const variation = variationTemplate.value[index];
            if (selected && !oldVal[index]) {
                compState.variationItem.push({
                    id: variation.id,
                    variation: variation.variation,
                    productData: variation.productData
                });
            } else if (!selected && oldVal[index]) {
                const itemIndex = compState.variationItem.findIndex(item => item.id === variation.id);
                if (itemIndex !== -1) {
                    compState.variationItem.splice(itemIndex, 1);
                }
            }
        });
    },
    { deep: true }
);

const compState = reactive({
    activeVariationID: '',
    required: false,
    sourceProduct: false,
    sourceProductData: {
        selected: false,
        id: '1',
        variation: 'Source',
        productData: {} as Product
    },
    variationItem: [] as VariationItem[],
})

const activeProduct = computed(() => {
    return productStore.getActiveProduct
})

const tempActiveProduct = computed(() => {
    return productStore.getTempActiveProduct
})

const activeProductId = computed(() => {
    return productStore.getActiveProductId
})

onMounted(() => {
    if (tempActiveProduct.value?.basic.productNumber) {
        const productGroup = tempActiveProduct.value
        const variationData = productGroup.variation
        compState.variationItem = variationData.variationItem
        compState.sourceProduct = variationData.sourceProduct
        compState.required = variationData.required
        variationTemplate.value.forEach((variation) => {
            variation.selected = !!variationData.variationItem.find(item => item.id === variation.id)
        })
        if (variationData.sourceProduct) {
            compState.sourceProductData.selected = true
            compState.sourceProductData.productData = tempActiveProduct.value
        } else {
            onClearSourceData()
        }
    }
})

const onProductSearchOpen = (activeVariationID: string) => {
    compState.activeVariationID = activeVariationID
}

const onSelect = (product: Product) => {
    const isProductAlreadySelected = variationTemplate.value.some(
        (variation) => variation.productData?.basic?.productNumber === product.basic.productNumber
    );

    if (compState.sourceProductData.productData.basic.productNumber === product.basic.productNumber) {
        Swal.fire({
            icon: 'warning',
            title: 'Warning',
            text: 'Cannot select source product.',
        });
        return;
    }

    if (!isProductAlreadySelected) {
        variationTemplate.value.forEach((variation) => {
            if (variation.id === compState.activeVariationID) {
                variation.productData = product;
                variation.selected = true;
            }
        });
    } else {
        Swal.fire({
            icon: 'warning',
            title: 'Warning',
            text: 'This product is already selected.',
        });
    }
}

const onClearSourceData = () => {
    compState.sourceProductData.productData = {} as Product
    compState.sourceProductData.selected = false
    compState.sourceProduct = false
    variationTemplate.value.forEach((variation) => {
        variation.productData = {} as Product
        variation.selected = false
    })
}

const onClearProductData = (variationID: string) => {
    variationTemplate.value.forEach((variation) => {
        if (variation.id === variationID) {
            variation.productData = {} as Product
            variation.selected = false
        }
    })
}

const onSourceClick = () => {
    compState.activeVariationID = compState.sourceProductData.id
    compState.sourceProduct = true
    compState.sourceProductData.productData = activeProduct.value
    compState.sourceProductData.selected = true
}

const canClearVariationData = (variationID: string) => {
    return variationTemplate.value.some(variation => variation.selected && variation.id === variationID);
}

watch(activeProductId, (newVal) => {
    if (newVal) {
        const productGroup = activeProduct.value
        const variationData = productGroup.variation
        compState.variationItem = variationData.variationItem
        compState.sourceProduct = variationData.sourceProduct
        compState.required = variationData.required
        if (variationData.sourceProduct) {
            compState.sourceProductData.selected = true
            compState.sourceProductData.productData = tempActiveProduct.value
        } else {
            onClearSourceData()
        }
    }
}, { immediate: true });

watch(compState, (newVal) => {
    productStore.setTempActiveProduct({
        ...tempActiveProduct.value,
        variation: {
            required: newVal.required,
            variationItem: newVal.variationItem,
            sourceProduct: newVal.sourceProduct
        }
    })

}, { deep: true });
</script>

<style scoped lang="scss">
.hover-shadow {
    transition: background-color 0.3s ease;
}

.hover-shadow:hover {
    background-color: rgba(0, 0, 0, 0.05);
}
</style>